import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronRight, ChevronLeft, Star, Ban, FileText, Activity, Paperclip, X, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { tendersAPI } from '../services/api';
import { logEvent } from '../utils/eventLog';
import { useBookmarks } from '../contexts/BookmarkContext';
import { useTenderReview } from '../contexts/TenderReviewContext';
import { useDisqualifications } from '../contexts/DisqualificationContext';
import DateRangeFilter from '../components/DateRangeFilter';
import CategoryFilter from '../components/CategoryFilter';
import './Dashboard.css';

// Source systems, in the analyst's language rather than the pipeline's.
// `pipeline-v2` is what the writer stamps on CanadaBuys records; nobody outside
// the pipeline should have to know that.
const SOURCE_LABELS = {
  'pipeline-v2': 'CanadaBuys',
  'bids-and-tenders': 'Municipal / police',
};

// Returns true for tenders in categories the team auto-disqualifies.
// "Construction" covers "Construction" and "Construction, Goods".
// "Services related to goods" is an exact match.
const AUTO_DISQUALIFY_CATEGORIES = ['construction', 'services related to goods'];
const isAutoDisqualifiedCategory = (tender) => {
  const cat = (
    tender['masterCategory-catégoriePrincipale'] ||
    tender.procurementCategory ||
    tender.procurement_category ||
    ''
  ).trim().toLowerCase();
  // Matched on the WHOLE category, not as a substring. The substring rule also
  // swallowed the mixed categories — 'Construction, Goods', 'Goods,
  // Construction', 'Construction, Services' and the three-way combinations.
  // Those carry a goods component and are exactly the work we bid on, so they
  // stay in Search; only a pure Construction or pure "Services related to
  // goods" listing is auto-dropped.
  return AUTO_DISQUALIFY_CATEGORIES.includes(cat);
};

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenders, setTenders] = useState([]);
  const [totalTenders, setTotalTenders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [maxScore, setMaxScore] = useState(0);

  // Initialize state from URL params or defaults
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('size')) || 25);
  const [searchType, setSearchType] = useState(searchParams.get('type') || 'hybrid');
  const [hideClosedTenders, setHideClosedTenders] = useState(searchParams.get('hideExpired') === 'true');
  const [hasPdfsFilter, setHasPdfsFilter] = useState(searchParams.get('hasPdfs') === 'true');
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'relevance');

  // Sprint 04-30 bulk select: lets the team mass-disqualify multiple tenders.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const { disqualify: bulkDisqualifyOne } = useDisqualifications();
  const toggleSelected = (tid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(tid) ? next.delete(tid) : next.add(tid);
      return next;
    });
  };
  const exitSelectionMode = () => { setSelectionMode(false); setSelectedIds(new Set()); };
  const bulkDisqualifySelected = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const targets = tenders.filter(
      (t) => selectedIds.has(t._id || t.tender_id || t.reference_number),
    );
    try {
      // Sequential to avoid spamming the API; the list is bounded by page size
      for (const t of targets) {
        try {
          await bulkDisqualifyOne({
            tender_id: t._id || t.tender_id || t.reference_number,
            title: t.title,
            organization: typeof t.organization === 'object'
              ? (t.organization?.contracting_entity || t.organization?.name)
              : t.organization,
            closing_date: t.closing_date || t.closingDate,
          });
        } catch (e) {
          console.warn('bulk disqualify (one) failed', t._id || t.tender_id, e);
        }
      }
    } finally {
      setBulkBusy(false);
      exitSelectionMode();
    }
  };

  // Date filter params (from URL — set by Home page stat cards)
  const fromDate = searchParams.get('from_date') || '';
  const toDate = searchParams.get('to_date') || '';
  const closingFrom = searchParams.get('closing_from') || '';
  const closingTo = searchParams.get('closing_to') || '';
  // Source system filter. In the URL so a filtered view can be shared and
  // survives a refresh, same as categories.
  const sourceFilter = searchParams.get('source') || '';
  // Sprint 04-30 P2: category filter (multi-select, comma-separated in URL)
  const selectedCategories = useMemo(() => {
    const raw = searchParams.get('categories') || '';
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);

  // Helper function to update URL params
  const updateUrlParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== '') {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    setSearchParams(params, { replace: true });
  };

  // NOTE: no separate mount-only effect. The dependency effect below already runs
  // on mount, so having both fired two concurrent identical searches on every page
  // load and whichever landed last won — which is what made results appear and
  // then vanish.
  useEffect(() => {
    fetchTenders();
    // selectedCategories is array — join for stable dep, since useMemo
    // returns a new ref only when the URL param changes.
  }, [currentPage, searchTerm, searchType, pageSize, hasPdfsFilter, hideClosedTenders, sortBy, fromDate, toDate, closingFrom, closingTo, selectedCategories.join(','), sourceFilter]);

  // Only the most recent fetch may commit. Without this a slower earlier request
  // can resolve after a newer one and overwrite good results — including blanking
  // them with [] from its catch block.
  const fetchSeqRef = useRef(0);

  const fetchTenders = async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const filters = {};
      if (hasPdfsFilter) filters.has_pdfs = true;
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;
      if (selectedCategories.length) filters.categories = selectedCategories;
      // Empty means every source — deliberately send nothing rather than a list
      // of all known sources, so a new source appears without a frontend change.
      if (sourceFilter) filters.sources = [sourceFilter];
      // Hide closed tenders SERVER-side. The render filter below still drops them
      // as a backstop, but a purely client-side hide removes rows *after* the
      // server has paginated, which is what produced short and empty pages.
      // An explicit closing_from from the date filter always wins.
      if (hideClosedTenders && !closingFrom) {
        filters.closing_date_from = new Date().toISOString().slice(0, 10);
      }
      if (closingFrom) filters.closing_date_from = closingFrom;
      if (closingTo) filters.closing_date_to = closingTo;

      const searchRequestParams = {
        page: currentPage,
        size: pageSize,
        sort_by: sortBy,
        filters
      };

      let response;

      if (searchType === 'semantic' && searchTerm.trim()) {
        // Use semantic search when search term exists and semantic mode is enabled
        response = await tendersAPI.semanticSearchTenders({
          query: searchTerm,
          ...searchRequestParams
        });
        setMaxScore(response.max_score || 0);
      } else if (searchType === 'keyword' && searchTerm.trim()) {
        // Use keyword-only search (strict matching, no fuzziness - best for acronyms)
        response = await tendersAPI.keywordSearchTenders({
          query: searchTerm,
          ...searchRequestParams
        });
        setMaxScore(response.max_score || 0);
      } else if (searchType === 'tender_id' && searchTerm.trim()) {
        // Use tender ID search for exact solicitation/reference number matching
        try {
          const tender = await tendersAPI.getTender(searchTerm.trim());
          response = {
            tenders: [tender],
            total: 1
          };
        } catch (error) {
          // If exact match fails, fallback to regular search for partial matches
          response = await tendersAPI.searchTenders({
            search: searchTerm,
            ...searchRequestParams
          });
        }
        setMaxScore(0);
      } else {
        // Use integrated search
        response = await tendersAPI.searchTenders({
          search: searchTerm,
          ...searchRequestParams
        });
        setMaxScore(0);
      }

      if (seq !== fetchSeqRef.current) return;   // superseded by a newer search
      setTenders(response.tenders || []);
      setTotalTenders(response.total || 0);
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;   // superseded — don't blank the list
      console.error('Error fetching tenders:', error);
      setTenders([]);
      setTotalTenders(0);
      setMaxScore(0);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newPage = 1;
    setCurrentPage(newPage);
    updateUrlParams({ q: searchTerm, page: newPage, type: searchType });
    logEvent('search.executed', {
      meta: {
        query: searchTerm,
        search_type: searchType,
        page_size: pageSize,
        has_filters: !!(selectedCategories?.length || fromDate || toDate || closingFrom || closingTo || hasPdfsFilter !== 'any'),
      },
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlParams({ page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    updateUrlParams({ type });
  };

  const handlePageSizeChange = (size) => {
    const newSize = parseInt(size);
    setPageSize(newSize);
    setCurrentPage(1);
    updateUrlParams({ size: newSize, page: 1 });
  };

  // Sprint 04-30 P1: hide tenders any teammate has saved or disqualified
  // from the search list immediately on click, without waiting for the
  // backend's 30-second hide-list cache to refresh. Backend filter is the
  // canonical hide; this is just for instant UX.
  const { isBookmarked: isBookmarkedForFilter, loading: bookmarksLoading } = useBookmarks();
  const { isDisqualified: isDisqualifiedForFilter, loading: disqualificationsLoading } = useDisqualifications();

  // Both context lists start empty and populate from their own async fetches, so
  // until they land isBookmarked/isDisqualified answer *false* for every tender.
  // Rendering before then paints the unfiltered result set and then visibly sheds
  // rows as the (10k+) disqualifications arrive — the "tenders appear then
  // disappear" report. Treat "filters not ready yet" as part of loading.
  // Safety valve: if either context never settles (unexpected throw before its
  // setLoading(false)), show results rather than spin forever. Worst case we're
  // back to the old flicker; we never trade it for a dead screen.
  const [filtersTimedOut, setFiltersTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFiltersTimedOut(true), 6000);
    return () => clearTimeout(t);
  }, []);

  const filtersReady = (!bookmarksLoading && !disqualificationsLoading) || filtersTimedOut;
  const listLoading = loading || !filtersReady;

  // Auto-disqualify construction-category tenders as they appear in search results.
  // Fires once per results batch; sequential to avoid API spam (same pattern as
  // bulkDisqualifySelected). isDisqualifiedForFilter is intentionally captured once
  // per tenders-change — stale reads only cause a redundant API call, not incorrect
  // state, since the backend de-dupes and disqualify() uses a functional setItems.
  useEffect(() => {
    // Waits on filtersReady too: firing before the disqualification list loads
    // means isDisqualifiedForFilter says false for everything, so we re-POST
    // disqualifications that already exist on every single search.
    if (listLoading || !tenders.length) return;
    const toAutoDisqualify = tenders.filter((t) => {
      const tid = t._id || t.tender_id || t.reference_number;
      return isAutoDisqualifiedCategory(t) && !isDisqualifiedForFilter(tid);
    });
    if (!toAutoDisqualify.length) return;
    let cancelled = false;
    (async () => {
      for (const t of toAutoDisqualify) {
        if (cancelled) break;
        try {
          await bulkDisqualifyOne({
            tender_id: t._id || t.tender_id || t.reference_number,
            title: t.title,
            organization: typeof t.organization === 'object'
              ? (t.organization?.contracting_entity || t.organization?.name)
              : t.organization,
            closing_date: t.closing_date || t.closingDate,
            reason: `Auto: ${t['masterCategory-catégoriePrincipale'] || t.procurementCategory || t.procurement_category || 'blocked category'}`,
          });
        } catch (e) {
          console.warn('auto-disqualify construction failed', t._id || t.tender_id, e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tenders, listLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredTenders = tenders.filter((tender) => {
    const tid = tender._id || tender.tender_id || tender.reference_number;
    if (isBookmarkedForFilter(tid) || isDisqualifiedForFilter(tid)) return false;
    if (isAutoDisqualifiedCategory(tender)) return false;
    if (!hideClosedTenders) return true;
    const closingDate = tender.closing_date || tender.closingDate || tender.close_date;
    if (!closingDate) return true; // Keep tenders with no closing date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const closeDate = new Date(closingDate);
    closeDate.setHours(0, 0, 0, 0);
    return closeDate >= today; // Only show if closing date is today or future
  });

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <div className="dashboard-content-full">
          <div className="search-controls">
            <form className="search-bar" onSubmit={handleSearch}>
              <Search className="search-icon" />
              <input
                type="search"
                placeholder="Search listings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="submit" className="stripe-button">
                Search
              </button>
            </form>

            {(fromDate || toDate || closingFrom || closingTo || sourceFilter) && (
              <div className="active-filters-banner">
                <span>
                  Filtered:
                  {fromDate && ` Published from ${fromDate}`}
                  {toDate && ` Published to ${toDate}`}
                  {closingFrom && ` Closing from ${closingFrom}`}
                  {closingTo && ` Closing to ${closingTo}`}
                  {sourceFilter && ` Source: ${SOURCE_LABELS[sourceFilter] || sourceFilter}`}
                </span>
                <button
                  className="clear-filters-btn"
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.delete('from_date');
                    params.delete('to_date');
                    params.delete('closing_from');
                    params.delete('closing_to');
                    params.delete('source');
                    params.delete('sort_by');
                    setSearchParams(params, { replace: true });
                    setSortBy('relevance');
                  }}
                >
                  Clear filters
                </button>
              </div>
            )}
            {/* Sprint 04-30 P2: compact icon-prefixed filter row.
                The verbose `LABEL:` callouts ate ~560px on a single line and
                forced the date filter to wrap once we added Category. Icons
                + value-only dropdowns keep everything inline + scannable. */}
            <div className="results-info compact-filters">
              <span className="results-count">
                {hideClosedTenders ? (
                  <>Showing <strong>{filteredTenders.length}</strong> open tenders (of {totalTenders.toLocaleString()})</>
                ) : (
                  <>Showing <strong>{tenders.length}</strong> of {totalTenders.toLocaleString()}</>
                )}
              </span>

              <div className="filter-chip" title="Tender source">
                <Building2 size={14} />
                <select
                  aria-label="Tender source"
                  className="filter-chip-select"
                  value={sourceFilter}
                  onChange={(e) => {
                    setCurrentPage(1);
                    updateUrlParams({ source: e.target.value });
                  }}
                >
                  <option value="">All sources</option>
                  <option value="pipeline-v2">CanadaBuys</option>
                  <option value="bids-and-tenders">Municipal / police</option>
                </select>
              </div>

              <div className="filter-chip" title="Search type">
                <Search size={14} />
                <select
                  aria-label="Search type"
                  className="filter-chip-select"
                  value={searchType}
                  onChange={(e) => handleSearchTypeChange(e.target.value)}
                >
                  <option value="hybrid">Hybrid</option>
                  <option value="keyword">Keyword</option>
                  <option value="semantic">AI / semantic</option>
                  <option value="tender_id">Tender ID</option>
                </select>
              </div>

              <div className="filter-chip" title="Results per page">
                <FileText size={14} />
                <select
                  aria-label="Results per page"
                  className="filter-chip-select"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                >
                  <option value="25">25 / page</option>
                  <option value="50">50 / page</option>
                  <option value="100">100 / page</option>
                </select>
              </div>

              <div className="filter-chip" title="Status">
                <Activity size={14} />
                <select
                  aria-label="Status"
                  className="filter-chip-select"
                  value={hideClosedTenders ? 'open' : 'all'}
                  onChange={(e) => {
                    const hide = e.target.value === 'open';
                    setHideClosedTenders(hide);
                    updateUrlParams({ hideExpired: hide ? 'true' : '' });
                  }}
                >
                  <option value="all">All status</option>
                  <option value="open">Open only</option>
                </select>
              </div>

              <div className="filter-chip" title="Documents">
                <Paperclip size={14} />
                <select
                  aria-label="Documents"
                  className="filter-chip-select"
                  value={hasPdfsFilter ? 'pdfs' : 'all'}
                  onChange={(e) => {
                    const wantPdfs = e.target.value === 'pdfs';
                    setHasPdfsFilter(wantPdfs);
                    setCurrentPage(1);
                    updateUrlParams({ hasPdfs: wantPdfs ? 'true' : '', page: 1 });
                  }}
                >
                  <option value="all">Any docs</option>
                  <option value="pdfs">Has PDFs</option>
                </select>
              </div>

              <CategoryFilter
                selected={selectedCategories}
                onApply={(cats) => {
                  setCurrentPage(1);
                  updateUrlParams({
                    categories: cats.length ? cats.join(',') : '',
                    page: '1',
                  });
                }}
              />

              <DateRangeFilter
                fromDate={fromDate}
                toDate={toDate}
                closingFrom={closingFrom}
                closingTo={closingTo}
                onApply={({ from_date, to_date, closing_from, closing_to }) => {
                  setCurrentPage(1);
                  updateUrlParams({
                    from_date, to_date, closing_from, closing_to, page: '1',
                  });
                }}
              />

              {searchType === 'semantic' && searchTerm.trim() && maxScore > 0 && (
                <div className="relevance-indicator">
                  Match: {(maxScore * 100).toFixed(1)}%
                </div>
              )}

              {/* Sprint 04-30 P2: Clear-all-filters button — only renders
                  when at least one filter is non-default. Resets state + URL
                  in one go so users can return to the unfiltered view
                  without clicking through every dropdown. */}
              {(hideClosedTenders || hasPdfsFilter || selectedCategories.length > 0
                || fromDate || toDate || closingFrom || closingTo) && (
                <button
                  type="button"
                  className="filter-chip filter-chip-clear"
                  onClick={() => {
                    setHideClosedTenders(false);
                    setHasPdfsFilter(false);
                    setCurrentPage(1);
                    updateUrlParams({
                      hideExpired: '',
                      hasPdfs: '',
                      categories: '',
                      from_date: '',
                      to_date: '',
                      closing_from: '',
                      closing_to: '',
                      page: '1',
                    });
                  }}
                  title="Clear all filters"
                >
                  <X size={14} />
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Bulk-select toolbar (Sprint 04-30 P1) */}
          <div className="bulk-toolbar">
            {!selectionMode ? (
              <button
                type="button"
                className="bulk-toolbar-btn"
                onClick={() => setSelectionMode(true)}
                disabled={listLoading || filteredTenders.length === 0}
              >
                ☑ Select tenders
              </button>
            ) : (
              <>
                <span className="bulk-toolbar-count">
                  {selectedIds.size} selected
                </span>
                <button
                  type="button"
                  className="bulk-toolbar-btn primary"
                  onClick={bulkDisqualifySelected}
                  disabled={selectedIds.size === 0 || bulkBusy}
                  title="Disqualify all selected tenders (team-wide)"
                >
                  <Ban size={12} /> {bulkBusy ? 'Disqualifying…' : `Disqualify ${selectedIds.size}`}
                </button>
                <button
                  type="button"
                  className="bulk-toolbar-btn"
                  onClick={() => setSelectedIds(new Set(filteredTenders.map(
                    (t) => t._id || t.tender_id || t.reference_number,
                  )))}
                  disabled={bulkBusy}
                >
                  Select all on page
                </button>
                <button
                  type="button"
                  className="bulk-toolbar-btn"
                  onClick={exitSelectionMode}
                  disabled={bulkBusy}
                >
                  Cancel
                </button>
              </>
            )}
          </div>

          {listLoading ? (
            <div className="loading">Loading tenders...</div>
          ) : (
            <>
              <div className="tenders-list">
                {filteredTenders.length === 0 && hideClosedTenders && tenders.length > 0 ? (
                  <div className="no-results-message">
                    <p>No open tenders on this page. All {tenders.length} tenders have passed their closing date.</p>
                    <p>Try the next page or disable the "Open Only" filter to see all results.</p>
                  </div>
                ) : (
                  filteredTenders.map((tender) => {
                    const tid = tender._id || tender.tender_id || tender.reference_number;
                    return (
                      <TenderCard
                        key={tid}
                        tender={tender}
                        showRelevance={searchType === 'semantic' && searchTerm.trim()}
                        selectionMode={selectionMode}
                        selected={selectedIds.has(tid)}
                        onToggleSelect={toggleSelected}
                      />
                    );
                  })
                )}
              </div>

              {totalTenders > pageSize && (
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalTenders}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  filteredCount={hideClosedTenders ? filteredTenders.length : null}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TenderCard = ({ tender, showRelevance = false, selectionMode = false, selected = false, onToggleSelect = null }) => {
  const {
    getTenderStatus,
    saveTender,
    unsaveTender,
    // Sprint 04-30 P1: markNotApplicable/isNotApplicable removed — DQ is
    // now driven by DisqualificationContext (team-shared backend index).
    // The local notApplicable state was leaving cards greyed-out after a
    // Restore because nothing in the codebase clears it.
    isSaved
  } = useTenderReview();
  const { addBookmark, removeBookmark, isBookmarked } = useBookmarks();
  const { isDisqualified, disqualify, undisqualify } = useDisqualifications();
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Date-only strings (e.g. "2026-02-21") are parsed as UTC by JS,
      // causing off-by-one in negative UTC offsets. Append T00:00:00 to
      // force local-time interpretation, matching the detail page.
      const normalized = dateString.length === 10 ? dateString + 'T00:00:00' : dateString;
      return format(new Date(normalized), 'MMM do, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const getDaysUntilClose = (closingDate) => {
    if (!closingDate) return null;
    try {
      const days = Math.ceil(
        (new Date(closingDate) - new Date()) / (1000 * 60 * 60 * 24)
      );
      return days;
    } catch {
      return null;
    }
  };

  // Enhanced closing date extraction with multiple field mapping
  const closingDate =
    tender.closing_date ||
    tender.closingDate ||
    tender.deadline ||
    tender.end_date ||
    tender.endDate ||
    tender.close_date ||
    tender.closeDate ||
    tender.bid_deadline ||
    tender.bidDeadline ||
    null;

  // Get status info
  const daysUntilClose = getDaysUntilClose(closingDate);
  const getStatusInfo = () => {
    const esStatus = tender.status?.toLowerCase();

    if (daysUntilClose !== null && daysUntilClose <= 0) {
      // Date has passed - check if ES still says Active (stale data warning)
      if (esStatus === 'active') {
        return { text: 'Closing Date Passed', class: 'status-date-passed' };
      }
      return { text: 'Closed', class: 'status-closed' };
    }

    if (daysUntilClose !== null && daysUntilClose <= 3) {
      return { text: 'Closing Soon', class: 'status-closing-soon' };
    }

    if (daysUntilClose !== null) {
      return { text: 'Open', class: 'status-open' };
    }

    return { text: tender.status || 'Open', class: 'status-open' };
  };

  const statusInfo = getStatusInfo();

  // Enhanced organization name extraction with multiple field mapping
  const organizationName =
    tender.organization?.contracting_entity ||
    tender.organization?.name ||
    tender.contracting_entity ||
    tender.buyer_name ||
    tender.department ||
    tender.organization_name ||
    (typeof tender.organization === 'string' ? tender.organization : null) ||
    'Government of Canada';

  const location = `${tender.location?.city || ''}, ${tender.location?.province || ''}`.replace(', ,', '').replace(/^, |, $/, '') || 'Canada';

  // Generate key indicators based on AI processing and PDF status
  const getKeyIndicators = (tender) => {
    const indicators = [];

    // Check PDF processing status with multiple field mappings
    const pdfStatus =
      tender.pdfProcessingStatus ||
      tender.pdf_processing_status ||
      tender.processing_status ||
      tender.status ||
      'pending';

    // Try to show AI-extracted indicators regardless of PDF status
    // Canadian requirements
    if (tender['canadianMade-fabriquéCanadien'] === 'yes' || tender['canadianMade-fabriquéCanadien'] === 'true') {
      indicators.push({ text: 'Canadian Made', type: 'canadian' });
    }

    // Indigenous business requirements
    if (tender['indigenousBusinessRequired-entrepriseAutochtooneRequise'] === 'true') {
      indicators.push({ text: 'Indigenous Preferred', type: 'indigenous' });
    }

    // Experience/References requirements
    if (tender['experienceRequired-expérienceRequise'] === 'true') {
      indicators.push({ text: 'References Required', type: 'experience' });
    }

    // Security clearance requirements
    if (tender.securityClearanceRequired === 'true' || tender.security_clearance_required === 'true') {
      indicators.push({ text: 'Security Clearance', type: 'security' });
    }

    // Sprint 04-30 P2: prefer the actual `procurementCategory` (set by the
    // Phase 1 scraper from the listing-row Category column) for category
    // indicators. The previous title-text heuristic flagged any tender with
    // "building" or "construction" in its title as Construction even when
    // the doc was filtered/categorized as Goods, which made the Goods filter
    // look broken.
    const category =
      tender.procurementCategory ||
      tender['masterCategory-catégoriePrincipale'] ||
      tender.procurement_category ||
      '';

    if (indicators.length === 0) {
      if (category) {
        // Use the structured category verbatim — it's reliable.
        indicators.push({ text: category, type: 'category' });
      } else {
        // Genuine "Unknown" — fall back to generic indicators.
        indicators.push({ text: 'Open Competition', type: 'general' });
        indicators.push({ text: 'Standard Terms', type: 'general' });
      }
    }

    return indicators;
  };

  // Deterministic price estimation based on tender ID hash (prevents changing on re-render)
  const getPriceEstimation = (tender) => {
    const title = tender.title?.toLowerCase() || '';
    const tenderId = tender._id || tender.tender_id || tender.reference_number || '';

    // Create a simple hash from tender ID for consistent "random" values
    let hash = 0;
    for (let i = 0; i < tenderId.length; i++) {
      hash = ((hash << 5) - hash) + tenderId.charCodeAt(i);
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Normalize hash to 0-1 range
    const normalizedHash = Math.abs(hash % 10000) / 10000;

    // Base estimation logic with deterministic variation
    let baseAmount = 250000; // Default base
    let range = 0;

    // Adjust based on keywords in title/description
    if (title.includes('infrastructure') || title.includes('construction')) {
      baseAmount = 2000000;
      range = 7000000; // 2-9M
    } else if (title.includes('software') || title.includes('it') || title.includes('digital')) {
      baseAmount = 1000000;
      range = 4000000; // 1-5M
    } else if (title.includes('consulting') || title.includes('advisory')) {
      baseAmount = 500000;
      range = 2000000; // 0.5-2.5M
    } else if (title.includes('maintenance') || title.includes('support')) {
      baseAmount = 300000;
      range = 1500000; // 0.3-1.8M
    } else if (title.includes('security') || title.includes('cybersecurity')) {
      baseAmount = 800000;
      range = 3000000; // 0.8-3.8M
    } else if (title.includes('research') || title.includes('laboratory')) {
      baseAmount = 1200000;
      range = 2500000; // 1.2-3.7M
    } else {
      baseAmount = 400000;
      range = 1800000; // 0.4-2.2M
    }

    return Math.floor(baseAmount + (normalizedHash * range));
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'TBD';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      notation: 'compact'
    }).format(amount);
  };

  const estimatedAmount = getPriceEstimation(tender);
  const keyIndicators = getKeyIndicators(tender);
  const referenceNumber = tender._id || tender.tender_id || tender.reference_number || 'N/A';
  const tenderId = tender._id || tender.tender_id || tender.reference_number;
  const currentStatus = getTenderStatus(tenderId);

  // Handle quick actions
  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isSaved(tenderId)) {
      // If already saved, unsave and remove from bookmarks
      unsaveTender(tenderId);
      removeBookmark(tenderId);
    } else {
      // Save tender and add to bookmarks
      saveTender(tender);
      addBookmark(tender);
    }
  };

  const handleDisqualify = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (isDisqualified(tenderId)) {
        await undisqualify(tenderId);
      } else {
        // Sprint 04-30 P1: persist to team-shared disqualifications index.
        // Don't touch TenderReviewContext.markNotApplicable — it persists
        // locally (e.g. localStorage) and used to leave the card greyed
        // out even after a Restore. The DisqualificationContext alone now
        // drives the UI: filteredTenders excludes disqualified ids
        // immediately after `disqualify` updates context state.
        if (isBookmarked(tenderId)) {
          removeBookmark(tenderId);
        }
        await disqualify({
          tender_id: tenderId,
          title: tender.title,
          organization: organizationName,
          closing_date: closingDate,
        });
      }
    } catch (err) {
      console.error('disqualify toggle failed', err);
    }
  };

  // In selection mode, clicking the card toggles select instead of navigating
  const handleCardClick = (e) => {
    if (selectionMode && onToggleSelect) {
      e.preventDefault();
      e.stopPropagation();
      onToggleSelect(tenderId);
    }
  };

  return (
    <div
      className={`tender-card-wrapper ${isSaved(tenderId) ? 'status-saved' : ''} ${selectionMode ? 'selection-mode' : ''} ${selected ? 'selected' : ''}`}
    >
      {selectionMode && (
        <input
          type="checkbox"
          className="bulk-select-checkbox"
          checked={selected}
          onChange={(e) => {
            e.stopPropagation();
            onToggleSelect && onToggleSelect(tenderId);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <Link
        to={selectionMode ? '#' : `/tender/${encodeURIComponent(tender._id || tender.tender_id || tender.reference_number)}`}
        className="tender-card"
        onClick={handleCardClick}
      >
        {/* Header with status, title and actions */}
        <div className="tender-card-header">
          <div className="header-left">
            <span className={`tender-status ${statusInfo.class}`}>
              {statusInfo.text}
            </span>
            <h3 className="tender-title">{tender.title || 'Untitled Tender'}</h3>
          </div>
          {/* Quick Action Buttons inline on right */}
          <div className="header-actions">
            <button
              className={`action-btn save-btn ${isSaved(tenderId) ? 'active' : ''}`}
              onClick={handleSave}
              title="Save this tender"
            >
              <Star size={12} fill={isSaved(tenderId) ? 'currentColor' : 'none'} />
              Save
            </button>

            <button
              className={`action-btn not-applicable-btn ${isDisqualified(tenderId) ? 'active' : ''}`}
              onClick={handleDisqualify}
              title={isDisqualified(tenderId) ? 'Restore from disqualified' : 'Disqualify (hide from team search)'}
            >
              <Ban size={12} />
              {isDisqualified(tenderId) ? 'Restore' : 'Disqualify'}
            </button>
          </div>
        </div>
        
        {/* Highlighted fields section - matching index9.html */}
        <div className="tender-highlights">
          <p className="highlight-field">
            <strong>Amount:</strong> {formatCurrency(estimatedAmount)} CAD
          </p>
          <p className="highlight-field">
            <strong>Published:</strong>{' '}
            {formatDate(
              tender.publish_date ||
              tender.publication_date ||
              tender.publicationDate ||
              tender.date,
            )}
          </p>
          <p className="highlight-field">
            <strong>Closing Date:</strong> {formatDate(closingDate)}
          </p>
        </div>

        {/* Two-column grid - matching index9.html structure */}
        <div className="tender-details-grid">
          <div className="tender-left-column">
            <p><strong>Category:</strong> {tender.procurementCategory || tender['masterCategory-catégoriePrincipale'] || tender.procurement_category || 'Unknown'}</p>
            <p><strong>Key Indicators:</strong> 
              <span className="indicators-inline">
                {keyIndicators.map((indicator, index) => (
                  <span key={index} className="indicator-reference">
                    {indicator.text}
                  </span>
                ))}
              </span>
            </p>
          </div>
          
          <div className="tender-right-column">
            <p><strong>Organization:</strong> {organizationName}</p>
            <p><strong>Reference:</strong> <span className="reference-number">{referenceNumber}</span></p>
          </div>
        </div>

      </Link>
    </div>
  );
};

const Pagination = ({ currentPage, totalItems, itemsPerPage, onPageChange, filteredCount }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Sprint 04-30 P2: jump-to-page input. The numeric page list elides
  // distant pages with "…" so getting to e.g. page 87/87 used to need
  // multiple clicks; this lets users type the target directly.
  const [jumpInput, setJumpInput] = useState('');
  const submitJump = (e) => {
    if (e) e.preventDefault();
    const n = parseInt(jumpInput, 10);
    if (Number.isFinite(n)) {
      const clamped = Math.min(Math.max(1, n), totalPages);
      onPageChange(clamped);
      setJumpInput('');
    }
  };

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="pagination">
      <div className="pagination-info">
        {filteredCount !== null ? (
          <>Showing {filteredCount} open tender{filteredCount !== 1 ? 's' : ''} on this page (page {currentPage} of {totalPages})</>
        ) : (
          <>Showing {startItem}-{endItem} of {totalItems.toLocaleString()} results</>
        )}
      </div>

      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          title="First page"
        >
          «
        </button>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft size={16} />
          Previous
        </button>

        <div className="pagination-numbers">
          {getVisiblePages().map((page, index) => (
            <button
              key={index}
              className={`pagination-number ${page === currentPage ? 'active' : ''} ${page === '...' ? 'dots' : ''}`}
              onClick={() => typeof page === 'number' && onPageChange(page)}
              disabled={page === '...'}
            >
              {page}
            </button>
          ))}
        </div>

        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
          <ChevronRight size={16} />
        </button>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          title="Last page"
        >
          »
        </button>

        <form className="pagination-jump" onSubmit={submitJump}>
          <label htmlFor="page-jump">Go to</label>
          <input
            id="page-jump"
            type="number"
            min={1}
            max={totalPages}
            placeholder={String(currentPage)}
            value={jumpInput}
            onChange={(e) => setJumpInput(e.target.value)}
            onBlur={() => jumpInput && submitJump()}
            aria-label={`Go to page (1-${totalPages})`}
          />
          <span className="pagination-jump-of">/ {totalPages.toLocaleString()}</span>
        </form>
      </div>
    </div>
  );
};

// Mock data for development
const getMockTenders = () => [
  {
    tender_id: "T001",
    title: "IT Infrastructure Modernization",
    description: "Seeking vendors for comprehensive IT infrastructure upgrade including cloud migration and cybersecurity enhancements.",
    organization: { name: "Public Services and Procurement Canada" },
    value: { amount: 2500000, currency: "CAD" },
    location: { city: "Ottawa", province: "ON" },
    closing_date: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
    status: "open",
    categories: ["Information Technology", "Cloud Services", "Cybersecurity"]
  },
  {
    tender_id: "T002",
    title: "Building Maintenance Services",
    description: "Annual contract for building maintenance services across multiple government facilities.",
    organization: { name: "Defence Construction Canada" },
    value: { amount: 850000, currency: "CAD" },
    location: { city: "Halifax", province: "NS" },
    closing_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    status: "open",
    categories: ["Facilities Management", "Maintenance", "Construction"]
  },
  {
    tender_id: "T003",
    title: "Translation Services",
    description: "Professional translation services for official government documents (English/French).",
    organization: { name: "Translation Bureau" },
    value: { amount: 150000, currency: "CAD" },
    location: { city: "Montreal", province: "QC" },
    closing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    status: "open",
    categories: ["Professional Services", "Translation", "Communications"]
  }
];

export default Dashboard;